import type { Level, Rect, Vec2 } from "../types";

/** focusP at/above this pinches corridors toward the cursor; below it, corridors relax.
 * Hunt's own pinch threshold — independent of isFocused's default (FOCUS_THRESHOLD_DEFAULT,
 * src/focus/gate.ts), which callers may set differently via StepInput.focusThreshold. */
const FOCUS_THRESHOLD = 0.7;
/** px/s pinch rate at focusP=1 (scaled down by (focusP-0.5) below that). */
const BASE_RATE_PX_S = 60;
/** How close the cursor must be to a rect's (expanded) bounds to be pinched. */
const NEAR_MARGIN_PX = 40;

export interface HuntState {
  /** Per level.corridors index: signed lateral offset (px) of the pinch, 0 = original rect. */
  offsets: number[];
}

export function resetHunt(level: Level): HuntState {
  return { offsets: level.corridors.map(() => 0) };
}

function narrowDim(r: Rect): number {
  return Math.min(r.w, r.h);
}

function isHorizontalRect(r: Rect): boolean {
  return r.w >= r.h;
}

function lateralCenter(r: Rect): number {
  return isHorizontalRect(r) ? r.y + r.h / 2 : r.x + r.w / 2;
}

function withLateral(r: Rect, center: number, width: number): Rect {
  if (isHorizontalRect(r)) {
    return { x: r.x, y: center - width / 2, w: r.w, h: width };
  }
  return { x: center - width / 2, y: r.y, w: width, h: r.h };
}

function isNear(r: Rect, p: Vec2, margin: number): boolean {
  return (
    p.x >= r.x - margin &&
    p.x <= r.x + r.w + margin &&
    p.y >= r.y - margin &&
    p.y <= r.y + r.h + margin
  );
}

function stepToward(cur: number, target: number, maxDelta: number): number {
  const d = target - cur;
  if (Math.abs(d) <= maxDelta) return target;
  return cur + Math.sign(d) * maxDelta;
}

/**
 * Pinch/relax `level.corridors` toward the cursor. Rects near the cursor narrow
 * (laterally, toward the cursor's side) while `focusP >= FOCUS_THRESHOLD` (this
 * module's own 0.7 pinch threshold, independent of isFocused's default in
 * src/focus/gate.ts), rate-limited and
 * always clamped so the resulting width never drops below `level.minWidth`.
 * When unfocused, every rect relaxes back toward its original shape at half the
 * base rate. `corridors` (the caller's current, possibly-pinched rects) is accepted
 * for API symmetry with the game loop; the pinch amount itself is derived from
 * `hunt.offsets` against the level's original rects so it stays stable/idempotent.
 */
export function applyHunt(
  level: Level,
  corridors: Rect[],
  cursor: Vec2,
  focusP: number,
  dtS: number,
  hunt: HuntState,
): { corridors: Rect[]; hunt: HuntState } {
  void corridors;
  const focused = focusP >= FOCUS_THRESHOLD;
  const offsets = level.corridors.map((_, i) => hunt.offsets[i] ?? 0);

  const nextCorridors = level.corridors.map((base, i) => {
    const dim = narrowDim(base);
    const maxOffset = Math.max(0, (dim - level.minWidth) / 2);
    const center = lateralCenter(base);
    const cursorLateral = isHorizontalRect(base) ? cursor.y : cursor.x;
    const near = isNear(base, cursor, NEAR_MARGIN_PX);

    let offset = offsets[i] ?? 0;
    if (focused && near && maxOffset > 0) {
      const rate = BASE_RATE_PX_S * (focusP - 0.5);
      const sign = Math.sign(cursorLateral - center) || 1;
      const target = sign * maxOffset;
      offset = stepToward(offset, target, Math.max(0, rate * dtS));
    } else {
      const relaxRate = BASE_RATE_PX_S / 2;
      offset = stepToward(offset, 0, Math.max(0, relaxRate * dtS));
    }
    offset = Math.max(-maxOffset, Math.min(maxOffset, offset));
    offsets[i] = offset;

    const newDim = Math.max(level.minWidth, dim - 2 * Math.abs(offset));
    return withLateral(base, center + offset, newDim);
  });

  return { corridors: nextCorridors, hunt: { offsets } };
}
